import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import L from 'leaflet';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { CATEGORY_ICON_NAMES, categoryIconComponent } from '@ci-ville/ui';
/**
 * Charte des points sur toutes les cartes CI-VILLE :
 * - un signalement : disque de la couleur du statut, bord blanc, icône de la catégorie en blanc ;
 * - un regroupement (groupe de points, municipalité, lieu récurrent) : disque plein avec le nombre.
 */
export const BRAND_BLUE = '#298FC2';
/**
 * Icônes du catalogue dessinées une fois, au chargement du module (hors rendu React),
 * puis recopiées en SVG dans les points ('' = épingle par défaut).
 */
const sprite = typeof document === 'undefined' ? null : document.createElement('div');
if (sprite) {
    const root = createRoot(sprite);
    flushSync(() => root.render(_jsx(_Fragment, { children: [...CATEGORY_ICON_NAMES, ''].map((name) => {
            const Icon = categoryIconComponent(name);
            return _jsx(Icon, { "data-icon": name, color: "white", strokeWidth: 2.25, "aria-hidden": true }, name);
        }) })));
}
const svgCache = new Map();
function categoryIconSvg(iconName, size) {
    const name = iconName && CATEGORY_ICON_NAMES.includes(iconName) ? iconName : '';
    const key = `${name}:${size}`;
    const cached = svgCache.get(key);
    if (cached !== undefined)
        return cached;
    const source = sprite?.querySelector(`[data-icon="${name}"]`);
    if (!source)
        return '';
    const svg = source.cloneNode(true);
    svg.removeAttribute('data-icon');
    svg.setAttribute('width', String(size));
    svg.setAttribute('height', String(size));
    svgCache.set(key, svg.outerHTML);
    return svg.outerHTML;
}
if (typeof document !== 'undefined' && !document.getElementById('ci-marker-styles')) {
    const style = document.createElement('style');
    style.id = 'ci-marker-styles';
    style.textContent = `
    @keyframes markerPulse {
      0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220,38,38,0.7); }
      50% { transform: scale(1.15); box-shadow: 0 0 0 12px rgba(220,38,38,0); }
    }
    .ci-marker-halo {
      position: absolute; inset: -6px; border-radius: 50%; border: 3px solid ${BRAND_BLUE}; pointer-events: none;
      animation: markerHalo 1.2s ease-out 3; opacity: 0;
    }
    @keyframes markerHalo {
      0% { transform: scale(0.8); opacity: 0.9; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) { .ci-marker div, .ci-marker-halo { animation: none !important; } }
  `;
    document.head.appendChild(style);
}
const disc = (size, color, shadow, extra = '') => `display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:50%;` +
    `background:${color};border:3px solid white;box-sizing:border-box;box-shadow:${shadow};${extra}`;
/** Point d'un signalement. */
export function reportMarkerIcon({ color, iconName, size = 30, highlighted = false, pulse = false, }) {
    const s = size + (highlighted ? 10 : 0);
    const shadow = highlighted ? `0 0 0 4px ${BRAND_BLUE}, 0 4px 12px rgba(0,0,0,0.45)` : '0 2px 8px rgba(0,0,0,0.3)';
    const animation = pulse && !highlighted ? 'animation: markerPulse 2s ease-in-out infinite;' : '';
    const halo = highlighted ? '<span class="ci-marker-halo"></span>' : '';
    return L.divIcon({
        className: 'ci-marker',
        html: `${halo}<div style="${disc(s, color, shadow, animation)}">${categoryIconSvg(iconName, Math.round(s * 0.5))}</div>`,
        iconSize: [s, s],
        iconAnchor: [s / 2, s / 2],
    });
}
/** Regroupement de signalements : le nombre au centre. */
export function groupMarkerIcon(count, color = BRAND_BLUE) {
    const s = count < 10 ? 32 : count < 100 ? 38 : 44;
    const font = count < 1000 ? 13 : 11;
    return L.divIcon({
        className: 'ci-marker',
        html: `<div style="${disc(s, color, '0 2px 8px rgba(0,0,0,0.3)', `color:white;font:700 ${font}px/1 system-ui,sans-serif;`)}">${count.toLocaleString('fr-CA')}</div>`,
        iconSize: [s, s],
        iconAnchor: [s / 2, s / 2],
    });
}
//# sourceMappingURL=map-markers.js.map